import { useFormState } from "common/hooks";
import { ReviewRating } from "common/types";

import TextInput from "client/common/components/inputs/TextInput";
import Modal from "client/common/components/deprecated-modals/Modal";
import TextArea from "client/common/components/inputs/TextArea";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import { useProductLineQuery } from "client/products/hooks";

import { useClientReviewCreateMutation } from "../hooks";
import ReviewStarsInput from "./ReviewStarsInput";

type ReviewCreateFormProps = {
  onClose: () => void;
  initialRating?: ReviewRating;
  productLineId: number;
};

const ReviewCreateForm = ({ onClose, initialRating, productLineId }: ReviewCreateFormProps) => {
  const { data: productLine, isLoading, error } = useProductLineQuery(productLineId);
  const [values, updateField] = useFormState({
    rating: initialRating,
    title: "",
    body: "",
  });
  const {
    mutate: createReview,
    isPending: isCreating,
    error: createError,
  } = useClientReviewCreateMutation({
    onSuccess: onClose,
  });

  const canSubmit = !isCreating && Object.values(values).every((val) => val);
  const submit = () => {
    const { rating } = values;
    // we know this will be true from canSubmit
    if (rating && productLine) {
      createReview({ ...values, rating, product_line_id: productLineId });
    }
  };

  if (isLoading) {
    return <ModalLayout.Loading />;
  }

  if (error || !productLine) {
    return <ModalLayout.FetchError entityName="product line" error={error} />;
  }

  return (
    <ModalLayout>
      <ModalLayout.Header title={`Review ${productLine.name}`} onClose={onClose} />
      <ModalLayout.Form
        canSubmit={canSubmit}
        onSubmit={submit}
        isLoading={isCreating}
        submitText="Submit Review"
        error={error}
      >
        <ReviewStarsInput
          value={values.rating}
          onChange={updateField("rating")}
          errors={createError?.errors.rating}
        />
        <TextInput
          label="Title"
          value={values.title}
          onChange={updateField("title")}
          errors={createError?.errors.title}
        />
        <TextArea
          label="Review"
          value={values.body}
          onChange={updateField("body")}
          errors={createError?.errors.body}
        />
      </ModalLayout.Form>
    </ModalLayout>
  );
};

type ReviewCreateModalProps = {
  visible: boolean;
  onClose: () => void;
  initialRating?: ReviewRating;
  productLineId: number;
};

const ReviewCreateModal = ({
  visible,
  onClose,
  initialRating,
  productLineId,
}: ReviewCreateModalProps) => {
  return (
    <Modal visible={visible} onClose={onClose}>
      <ReviewCreateForm
        key={productLineId}
        onClose={onClose}
        initialRating={initialRating}
        productLineId={productLineId}
      />
    </Modal>
  );
};

export default ReviewCreateModal;
